import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { EUROASIANN_OFFER_LABEL } from '@euroasiann/shared';
import { formatPdfCurrencyAmount, pdfCurrencyMark } from '../lib/currency';
import { formatEuroasiannBankDetailsBlock } from '../constants/euroasiann-banking';
import { buildOfferTermsContext, getOfferSaleTermsPageUrl } from '../constants/offer-sale-terms';

const BRAND_COLOR: [number, number, number] = [10, 36, 99];
const ACCENT_COLOR: [number, number, number] = [0, 136, 204];
const LIGHT_BG: [number, number, number] = [232, 244, 251];
const PANEL_BG: [number, number, number] = [240, 245, 255];
const TEXT_DARK: [number, number, number] = [30, 30, 30];
const TEXT_MUTED: [number, number, number] = [100, 100, 110];
const DIVIDER: [number, number, number] = [200, 210, 230];
const MARGIN = 14;
/** Footer rule ~pageHeight-14 and footer text ~pageHeight-8 — keep a modest buffer only. */
const FOOTER_SPACE = 18;

export interface AdminOfferItem {
  description?: string;
  impaNo?: string;
  partNo?: string;
  altPartNo?: string;
  drawingNumber?: string;
  positionNo?: string;
  dimensions?: string;
  offeredQty?: number | string;
  requiredQty?: number | string;
  quotedPrice?: number | string;
  unitPrice?: number | string;
  uom?: string;
  quality?: string;
  generalRemark?: string;
}

export interface AdminOfferLike {
  quotationNumber?: string;
  title?: string;
  description?: string;
  totalAmount?: number;
  currency?: string;
  createdAt?: string;
  status?: string;
  items?: AdminOfferItem[];
  metadata?: {
    recipientEmail?: string;
    recipientName?: string;
    vesselName?: string;
    imoNumber?: string;
    category?: string;
    subCategory?: string;
    brand?: string;
    model?: string;
    department?: string;
    equipmentTags?: string;
    revisionNo?: string;
    requisitionRef?: string;
    urgencyLevel?: string;
    contactName?: string;
    contactEmail?: string;
    contactPhone?: string;
    contactDepartment?: string;
    quoteDeadline?: string;
    quoteValidityDays?: string;
    leadDate?: string;
    remarks?: string;
    typeOfIncoterms?: string;
    typeOfLogisticContainer?: string;
    partialQuoteAllowed?: string;
    splitOrderAllowed?: string;
    requiredPaymentTerms?: string;
    requiredCertificates?: string[];
    requiredDocuments?: string[];
    packingInstructions?: string;
    partialDeliveryAllowed?: string;
    countryOfOrigin?: string;
    companyLogoUrl?: string | null;
    companyNameForLetterhead?: string;
    terms?: Record<string, string | number | undefined>;
    items?: AdminOfferItem[];
    taxConfig?: {
      enabled?: boolean;
      taxRate?: number | string;
      taxAmount?: number;
      subtotal?: number;
    };
    taxRate?: number | string;
    taxAmount?: number;
    subtotal?: number;
  };
}

function fmtDate(val?: string): string {
  if (!val || val === '-') return '-';
  try {
    return new Date(val).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  } catch {
    return val;
  }
}

function fmtList(values?: string[]): string {
  return values?.length ? values.join(', ') : '-';
}

function sectionHeader(doc: jsPDF, title: string, y: number, pageWidth: number, minBodySpace = 8): number {
  // Only reserve the header bar + a small lead-in. Large minBodySpace caused early
  // page breaks (e.g. empty gap after §4 before §5 on the next page).
  y = ensureSpace(doc, y, 12 + minBodySpace);
  const headerH = 7;
  doc.setFillColor(...BRAND_COLOR);
  doc.rect(MARGIN, y, pageWidth - MARGIN * 2, headerH, 'F');
  doc.setFontSize(8.5);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(255, 255, 255);
  doc.text(title.toUpperCase(), MARGIN + 3, y + 4.8);
  doc.setTextColor(...TEXT_DARK);
  // Margin below every section banner (e.g. "2. RECIPIENT & CONTACT") before field rows
  const marginBottom = 5.5;
  return y + headerH + marginBottom;
}

function labelValue(
  doc: jsPDF,
  label: string,
  value: string,
  x: number,
  y: number,
  labelWidth = 42,
  maxWidth?: number
): number {
  doc.setFontSize(7.5);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...TEXT_MUTED);
  doc.text(`${label}:`, x, y);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...TEXT_DARK);
  const wrapped = doc.splitTextToSize(value || '-', (maxWidth ?? 85) - labelWidth);
  doc.text(wrapped, x + labelWidth, y);
  return y + Math.max(4.5, wrapped.length * 3.6);
}

function ensureSpace(doc: jsPDF, y: number, needed: number): number {
  const pageHeight = doc.internal.pageSize.height;
  if (y + needed > pageHeight - FOOTER_SPACE) {
    doc.addPage();
    return MARGIN;
  }
  return y;
}

function drawWrappedBlock(
  doc: jsPDF,
  text: string,
  x: number,
  y: number,
  maxWidth: number,
  lineHeight = 3.8,
  fontSize = 7
): number {
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(fontSize);
  doc.setTextColor(...TEXT_DARK);
  const lines = doc.splitTextToSize(text, maxWidth);
  for (const line of lines) {
    y = ensureSpace(doc, y, lineHeight + 1);
    doc.text(line, x, y);
    y += lineHeight;
  }
  return y;
}

function drawSummaryBar(doc: jsPDF, y: number, pageWidth: number, cells: Array<{ label: string; value: string }>): number {
  const cols = 4;
  const colW = (pageWidth - MARGIN * 2) / cols;
  const rows = Math.ceil(cells.length / cols);
  const labelH = 3.2;
  const valueLineH = 3.6;
  const cellPadY = 3;
  const rowGap = 2.5;

  // Measure each cell so long values (e.g. vessel name) get enough vertical space
  const cellMeta = cells.map((cell) => {
    doc.setFontSize(8);
    doc.setFont('helvetica', 'bold');
    const wrapped = doc.splitTextToSize(String(cell.value || '-'), colW - 6);
    const valueH = Math.max(valueLineH, wrapped.length * valueLineH);
    return { ...cell, wrapped, height: labelH + 1.5 + valueH };
  });

  const rowHeights: number[] = [];
  for (let r = 0; r < rows; r++) {
    let maxH = 0;
    for (let c = 0; c < cols; c++) {
      const idx = r * cols + c;
      if (idx < cellMeta.length) maxH = Math.max(maxH, cellMeta[idx].height);
    }
    rowHeights.push(maxH + cellPadY);
  }

  const barH = rowHeights.reduce((sum, h) => sum + h, 0) + rowGap * Math.max(0, rows - 1) + 6;
  doc.setFillColor(...PANEL_BG);
  doc.rect(MARGIN, y, pageWidth - MARGIN * 2, barH, 'F');
  doc.setDrawColor(...ACCENT_COLOR);
  doc.setLineWidth(0.4);
  doc.rect(MARGIN, y, pageWidth - MARGIN * 2, barH, 'S');

  let rowY = y + 4;
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      const idx = r * cols + c;
      if (idx >= cellMeta.length) continue;
      const cell = cellMeta[idx];
      const sx = MARGIN + 3 + c * colW;
      doc.setFontSize(6.5);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(...TEXT_MUTED);
      doc.text(cell.label.toUpperCase(), sx, rowY + 2.5);
      doc.setFontSize(8);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(...BRAND_COLOR);
      doc.text(cell.wrapped, sx, rowY + 2.5 + labelH + 1.5);
    }
    rowY += rowHeights[r] + (r < rows - 1 ? rowGap : 0);
  }

  return y + barH + 4;
}

function resolveLineItems(offer: AdminOfferLike): AdminOfferItem[] {
  return (offer.items?.length ? offer.items : offer.metadata?.items) || [];
}

function resolveOfferNumber(offer: AdminOfferLike): string {
  const num = offer.quotationNumber?.trim();
  if (!num || num === 'DRAFT' || num === 'PREVIEW') return '—';
  return num;
}

function drawBankingSection(doc: jsPDF, y: number, pageWidth: number): number {
  const cardPaddingBottom = 5;
  const cardPaddingTop = 5;
  const lineSpacing = 4.2;
  const bankLines = formatEuroasiannBankDetailsBlock();
  const cardHeight = cardPaddingTop + 5 + bankLines.length * lineSpacing + cardPaddingBottom;

  y = sectionHeader(doc, 'Banking Details — Payment Instructions', y, pageWidth, 8);
  y = ensureSpace(doc, y, Math.min(cardHeight, 40));
  doc.setFillColor(...LIGHT_BG);
  doc.setDrawColor(...ACCENT_COLOR);
  doc.roundedRect(MARGIN, y, pageWidth - MARGIN * 2, cardHeight, 2, 2, 'FD');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8);
  doc.setTextColor(...BRAND_COLOR);
  doc.text('Remit payment to Euroasiann Marine Spares Pvt Ltd', MARGIN + 4, y + cardPaddingTop);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7.5);
  doc.setTextColor(...TEXT_DARK);
  let by = y + cardPaddingTop + 5;
  for (const line of bankLines) {
    doc.text(line, MARGIN + 4, by);
    by += lineSpacing;
  }
  return y + cardHeight + 4;
}

function drawTermsLinkSection(doc: jsPDF, y: number, pageWidth: number): number {
  y = sectionHeader(doc, 'Terms and Conditions of Sale', y, pageWidth, 8);

  const termsUrl = getOfferSaleTermsPageUrl();
  y = drawWrappedBlock(
    doc,
    'This offer is issued subject to the Euroasiann Offer Terms and Conditions of Sale. The complete Sale Terms are published online at the link below. Transaction-specific details for this offer are stated in Section 9 above.',
    MARGIN,
    y,
    pageWidth - MARGIN * 2,
    3.8,
    7.5
  );
  y += 3;

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8);
  doc.setTextColor(...ACCENT_COLOR);
  if (typeof (doc as jsPDF & { textWithLink?: Function }).textWithLink === 'function') {
    (doc as jsPDF & { textWithLink: (text: string, x: number, yPos: number, opts: { url: string }) => void }).textWithLink(
      termsUrl,
      MARGIN,
      y,
      { url: termsUrl }
    );
  } else {
    doc.text(termsUrl, MARGIN, y);
  }
  y += 6;

  doc.setFont('helvetica', 'italic');
  doc.setFontSize(7);
  doc.setTextColor(...TEXT_MUTED);
  y = drawWrappedBlock(
    doc,
    'Acceptance of this offer constitutes acceptance of those Sale Terms.',
    MARGIN,
    y,
    pageWidth - MARGIN * 2,
    3.6,
    7
  );
  return y + 4;
}

async function buildOfferPdfDoc(offer: AdminOfferLike): Promise<jsPDF> {
  const doc = new jsPDF({ unit: 'mm', format: 'a4' });
  const pageWidth = doc.internal.pageSize.width;
  const meta = offer.metadata || {};
  const terms = meta.terms || {};
  const lineItems = resolveLineItems(offer);
  const offerNumber = resolveOfferNumber(offer);
  const currency = offer.currency || String(terms.currency || 'USD');
  const createdAt = offer.createdAt || new Date().toISOString();
  const termsCtx = buildOfferTermsContext(offer);
  const vesselNameDisplay = String(meta.vesselName || '-').toUpperCase();

  const { drawEuroasiannGroupOfferLetterhead, drawPlatformPdfFooter } = await import('./document-letterhead');

  let y = await drawEuroasiannGroupOfferLetterhead(doc, {
    docTitle: EUROASIANN_OFFER_LABEL.toUpperCase(),
    accentRgb: BRAND_COLOR,
    offerMeta: {
      offerNumber,
      date: fmtDate(createdAt),
      validUntil: termsCtx.offerValidUntil,
    },
  });

  y += 2;

  y = drawSummaryBar(doc, y, pageWidth, [
    { label: 'Buyer / Recipient', value: meta.recipientName || meta.recipientEmail || '-' },
    { label: 'Vessel', value: vesselNameDisplay },
    { label: 'Currency', value: currency },
    { label: 'Total Offer', value: formatPdfCurrencyAmount(Number(offer.totalAmount) || 0, currency) },
    { label: 'Credit Type', value: String(terms.creditType || '-') },
    { label: 'Payment Term', value: String(terms.paymentTerm || meta.requiredPaymentTerms || '-') },
    { label: 'Delivery Term', value: String(terms.deliveryTerm || '-') },
    { label: 'Urgency', value: meta.urgencyLevel || '-' },
  ]);

  y = sectionHeader(doc, '1. Offer & General Information', y, pageWidth);
  const half = (pageWidth - MARGIN * 2) / 2;
  y = labelValue(doc, 'Offer Title', offer.title || '-', MARGIN, y, 38, half - 4);
  y = labelValue(doc, 'Requisition Ref', meta.requisitionRef || '-', MARGIN, y);
  y = labelValue(doc, 'Revision No', meta.revisionNo || '00', MARGIN + half, y - 9);
  y = labelValue(doc, 'Department', meta.department?.toUpperCase() || '-', MARGIN + half, y);
  y = labelValue(doc, 'Quote Deadline', fmtDate(meta.quoteDeadline), MARGIN, y);
  y = labelValue(doc, 'Quote Validity', meta.quoteValidityDays ? `${meta.quoteValidityDays} days` : '-', MARGIN + half, y - 4.5);
  y = labelValue(doc, 'Lead Date', fmtDate(meta.leadDate), MARGIN + half, y);
  y += 2;

  y = sectionHeader(doc, '2. Recipient & Contact', y, pageWidth);
  y = labelValue(doc, 'Recipient Name', meta.recipientName || '-', MARGIN, y);
  y = labelValue(doc, 'Recipient Email', meta.recipientEmail || '-', MARGIN + half, y - 4.5);
  y = labelValue(doc, 'Contact Person', meta.contactName || '-', MARGIN, y);
  y = labelValue(doc, 'Contact Dept', meta.contactDepartment || '-', MARGIN + half, y - 4.5);
  y = labelValue(doc, 'Contact Email', meta.contactEmail || '-', MARGIN, y);
  y = labelValue(doc, 'Contact Phone', meta.contactPhone || '-', MARGIN + half, y - 4.5);
  y += 2;

  y = sectionHeader(doc, '3. Vessel & Equipment', y, pageWidth);
  // Track left/right columns separately so a long vessel name does not collide with IMO / payment fields
  const vesselY = labelValue(doc, 'Vessel Name', vesselNameDisplay, MARGIN, y, 38, half - 4);
  const imoY = labelValue(doc, 'IMO Number', meta.imoNumber || '-', MARGIN + half, y, 38, half - 4);
  y = Math.max(vesselY, imoY) + 1;
  const brandY = labelValue(doc, 'Brand', meta.brand || '-', MARGIN, y, 38, half - 4);
  const modelY = labelValue(doc, 'Model', meta.model || '-', MARGIN + half, y, 38, half - 4);
  y = Math.max(brandY, modelY) + 1;
  const catY = labelValue(doc, 'Category', meta.category || '-', MARGIN, y, 38, half - 4);
  const subY = labelValue(doc, 'Sub Category', meta.subCategory || '-', MARGIN + half, y, 38, half - 4);
  y = Math.max(catY, subY) + 1;
  y = labelValue(doc, 'Equipment Tags', meta.equipmentTags || '-', MARGIN, y, 38, pageWidth - MARGIN * 2);
  y += 2;

  y = sectionHeader(doc, '4. Logistics & Procurement', y, pageWidth, 8);
  y = labelValue(doc, 'Incoterms', meta.typeOfIncoterms || '-', MARGIN, y);
  y = labelValue(doc, 'Logistic Container', meta.typeOfLogisticContainer || '-', MARGIN + half, y - 4.5);
  y = labelValue(doc, 'Transport Term', String(terms.transportTerm || '-'), MARGIN, y);
  y = labelValue(doc, 'Packing Term', String(terms.packingTerm || '-'), MARGIN + half, y - 4.5);
  y = labelValue(doc, 'Partial Quote', meta.partialQuoteAllowed || '-', MARGIN, y);
  y = labelValue(doc, 'Split Order', meta.splitOrderAllowed || '-', MARGIN + half, y - 4.5);
  y = labelValue(doc, 'Partial Delivery', meta.partialDeliveryAllowed || '-', MARGIN, y);
  y += 2;

  // Flow continuously after §4 — only break if the §5 header itself won't fit.
  y = sectionHeader(doc, '5. Commercial Terms & Conditions', y, pageWidth, 10);
  const termRows = [
    ['Credit Type', String(terms.creditType || '-')],
    ['Payment Term', String(terms.paymentTerm || '-')],
    ['Insurance Term', String(terms.insuranceTerm || '-')],
    ['Tax Term', String(terms.taxTerm || '-')],
    ['Transport Term', String(terms.transportTerm || '-')],
    ['Delivery Term', String(terms.deliveryTerm || '-')],
    ['Packing Term', String(terms.packingTerm || '-')],
    ['Required Payment', meta.requiredPaymentTerms || '-'],
  ];
  autoTable(doc, {
    startY: y,
    head: [['Term', 'Agreed Value']],
    body: termRows,
    theme: 'grid',
    headStyles: { fillColor: LIGHT_BG, textColor: BRAND_COLOR, fontStyle: 'bold', fontSize: 7 },
    styles: { fontSize: 7, cellPadding: 1.6, textColor: TEXT_DARK, lineColor: DIVIDER },
    margin: { left: MARGIN, right: MARGIN, bottom: FOOTER_SPACE },
    columnStyles: { 0: { cellWidth: 45, fontStyle: 'bold' }, 1: { cellWidth: 'auto' } },
  });
  y = (doc as any).lastAutoTable.finalY + 3;

  y = sectionHeader(doc, '6. Required Certificates & Documents', y, pageWidth, 8);
  y = labelValue(doc, 'Certificates', fmtList(meta.requiredCertificates), MARGIN, y, 38, pageWidth - MARGIN * 2);
  y = labelValue(doc, 'Documents', fmtList(meta.requiredDocuments), MARGIN, y, 38, pageWidth - MARGIN * 2);
  y = labelValue(doc, 'Packing Instructions', meta.packingInstructions || '-', MARGIN, y, 38, pageWidth - MARGIN * 2);
  y += 2;

  y = sectionHeader(doc, '7. Offered Items', y, pageWidth, 10);

  const tableData = lineItems.map((item, index) => {
    const qty = Number(item.offeredQty ?? item.requiredQty ?? 0) || 0;
    const unitPrice = Number(item.quotedPrice ?? item.unitPrice ?? 0) || 0;
    const partBits = [item.partNo, item.altPartNo ? `Alt:${item.altPartNo}` : ''].filter(Boolean).join(' / ');
    return [
      String(index + 1),
      item.impaNo || '-',
      item.description || '-',
      partBits || '-',
      item.drawingNumber || '-',
      item.positionNo || '-',
      item.dimensions || '-',
      String(qty),
      item.uom || '-',
      item.quality || '-',
      unitPrice.toLocaleString(undefined, { minimumFractionDigits: 2 }),
      (qty * unitPrice).toLocaleString(undefined, { minimumFractionDigits: 2 }),
      item.generalRemark || '-',
    ];
  });

  autoTable(doc, {
    startY: y,
    head: [['#', 'IMPA', 'Description', 'Part No', 'Drw', 'Pos', 'Dim', 'Qty', 'UoM', 'Quality', `Unit Price (${pdfCurrencyMark(currency)})`, `Amount (${pdfCurrencyMark(currency)})`, 'Remarks']],
    body: tableData.length ? tableData : [['-', '-', 'No items', '-', '-', '-', '-', '-', '-', '-', '-', '-', '-']],
    theme: 'grid',
    headStyles: { fillColor: BRAND_COLOR, textColor: [255, 255, 255], fontStyle: 'bold', fontSize: 6 },
    styles: { fontSize: 6, cellPadding: 1.5, textColor: TEXT_DARK, lineColor: DIVIDER },
    margin: { left: MARGIN, right: MARGIN, bottom: FOOTER_SPACE },
  });
  y = (doc as any).lastAutoTable.finalY + 3;

  const subtotal = lineItems.reduce((sum, item) => {
    const qty = Number(item.offeredQty ?? item.requiredQty ?? 0) || 0;
    const unitPrice = Number(item.quotedPrice ?? item.unitPrice ?? 0) || 0;
    return sum + qty * unitPrice;
  }, 0);

  const metaTax = meta.taxConfig;
  const hasTax = Boolean(metaTax?.enabled && (Number(metaTax?.taxRate) > 0 || Number(metaTax?.taxAmount) > 0));
  const taxRateVal = hasTax ? Number(metaTax?.taxRate) : 0;
  const taxAmountVal = hasTax ? (Number(metaTax?.taxAmount) || (subtotal * taxRateVal) / 100) : 0;
  const finalAmount = Number(offer.totalAmount) || (subtotal + taxAmountVal);

  const totalsW = 78;
  const totalsX = pageWidth - MARGIN - totalsW;

  if (hasTax && taxAmountVal > 0) {
    const totalsH = 25;
    y = ensureSpace(doc, y, totalsH + 2);
    doc.setFillColor(...LIGHT_BG);
    doc.setDrawColor(...ACCENT_COLOR);
    doc.roundedRect(totalsX, y, totalsW, totalsH, 2, 2, 'FD');

    // Subtotal
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7.5);
    doc.setTextColor(...TEXT_DARK);
    doc.text('Subtotal:', totalsX + 4, y + 5.5);
    doc.text(`${currency} ${subtotal.toLocaleString(undefined, { minimumFractionDigits: 2 })}`, totalsX + totalsW - 4, y + 5.5, { align: 'right' });

    // Tax
    doc.text(`Tax (${taxRateVal}%):`, totalsX + 4, y + 11.5);
    doc.text(`${currency} ${taxAmountVal.toLocaleString(undefined, { minimumFractionDigits: 2 })}`, totalsX + totalsW - 4, y + 11.5, { align: 'right' });

    // Divider line
    doc.setDrawColor(...DIVIDER);
    doc.line(totalsX + 4, y + 14.5, totalsX + totalsW - 4, y + 14.5);

    // Total Offer Amount
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8);
    doc.setTextColor(...BRAND_COLOR);
    doc.text('TOTAL OFFER AMOUNT', totalsX + 4, y + 20);
    doc.setFontSize(9.5);
    doc.text(`${currency} ${finalAmount.toLocaleString(undefined, { minimumFractionDigits: 2 })}`, totalsX + totalsW - 4, y + 20, { align: 'right' });
    y += totalsH + 4;
  } else {
    y = ensureSpace(doc, y, 16);
    doc.setFillColor(...BRAND_COLOR);
    doc.roundedRect(totalsX, y, totalsW, 14, 2, 2, 'F');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8);
    doc.setTextColor(255, 255, 255);
    doc.text('TOTAL OFFER AMOUNT', totalsX + 4, y + 5);
    doc.setFontSize(11);
    doc.text(`${currency} ${finalAmount.toLocaleString(undefined, { minimumFractionDigits: 2 })}`, totalsX + totalsW - 4, y + 11.5, { align: 'right' });
    y += 16;
  }

  if (meta.remarks || offer.description) {
    y = sectionHeader(doc, '8. Remarks', y, pageWidth, 8);
    y = drawWrappedBlock(doc, String(meta.remarks || offer.description), MARGIN, y, pageWidth - MARGIN * 2);
    y += 2;
  }

  y = sectionHeader(doc, '9. Transaction-Specific Information (Sale Terms, Section 52)', y, pageWidth, 8);
  const txLines = [
    `Offer Number: ${termsCtx.offerNumber}`,
    `Offer Date: ${termsCtx.offerDate}`,
    `Buyer: ${termsCtx.buyer}`,
    `Vessel: ${termsCtx.vessel} | IMO: ${termsCtx.imoNumber}`,
    `Goods: ${termsCtx.goods}`,
    `Quantity: ${termsCtx.quantity}`,
    `Total Price: ${termsCtx.totalPrice}`,
    `Payment Terms: ${termsCtx.paymentTerms}`,
    `Delivery / Incoterm: ${termsCtx.deliveryLocation} | ${termsCtx.incoterm}`,
    `Lead Time / Valid Until: ${termsCtx.estimatedLeadTime} | ${termsCtx.offerValidUntil}`,
    `Warranty: ${termsCtx.warranty}`,
    `Product Condition: ${termsCtx.productCondition}`,
    `Special Conditions: ${termsCtx.specialConditions}`,
  ];

  const txMaxWidth = pageWidth - MARGIN * 2 - 8;
  const txLineH = 3.8;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7);
  const txWrapped = txLines.map((line) => doc.splitTextToSize(String(line || '-'), txMaxWidth) as string[]);
  const txContentH = txWrapped.reduce((sum, lines) => sum + Math.max(1, lines.length) * txLineH, 0);
  const boxPadTop = 5;
  const boxPadBottom = 5;
  const boxH = boxPadTop + txContentH + boxPadBottom;

  y = ensureSpace(doc, y, boxH + 2);
  doc.setFillColor(...PANEL_BG);
  doc.setDrawColor(...ACCENT_COLOR);
  doc.roundedRect(MARGIN, y, pageWidth - MARGIN * 2, boxH, 2, 2, 'FD');
  doc.setTextColor(...TEXT_DARK);
  let ty = y + boxPadTop + 2.5;
  for (const lines of txWrapped) {
    for (const line of lines) {
      doc.text(line, MARGIN + 4, ty);
      ty += txLineH;
    }
  }
  y = y + boxH + 3;

  y = drawTermsLinkSection(doc, y, pageWidth);
  y = drawBankingSection(doc, y, pageWidth);

  const pageCount = doc.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    drawPlatformPdfFooter(doc, { center: `Offer: ${offerNumber}` });
  }

  return doc;
}

export async function buildAdminOfferPdfBlob(offer: AdminOfferLike): Promise<Blob> {
  const doc = await buildOfferPdfDoc(offer);
  return doc.output('blob');
}

/** Convert a PDF blob to raw base64 (no data: prefix) for email attachment upload. */
export async function adminOfferPdfBlobToBase64(blob: Blob): Promise<string> {
  const buffer = await blob.arrayBuffer();
  const bytes = new Uint8Array(buffer);
  const chunkSize = 0x8000;
  let binary = '';
  for (let i = 0; i < bytes.length; i += chunkSize) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunkSize));
  }
  return btoa(binary);
}

export async function downloadAdminOfferPDF(offer: AdminOfferLike) {
  const doc = await buildOfferPdfDoc(offer);
  const offerNumber = resolveOfferNumber(offer);
  doc.save(`${EUROASIANN_OFFER_LABEL.replace(/\s+/g, '_')}_${offerNumber}.pdf`);
}

export async function viewAdminOfferPDF(offer: AdminOfferLike) {
  const blob = await buildAdminOfferPdfBlob(offer);
  const url = URL.createObjectURL(blob);
  window.open(url, '_blank', 'noopener,noreferrer');
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

export function buildOfferQuotationData(offer: AdminOfferLike) {
  return offer;
}
